'use client';

import { useEffect, useRef } from 'react';

interface MobileScrollIndicatorProps {
  scrollContainerId: string;
}

export function MobileScrollIndicator({
  scrollContainerId,
}: MobileScrollIndicatorProps) {
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!progressRef.current) return;

      const container = document.getElementById(scrollContainerId);

      let bestScrollTop = 0;
      let bestMaxScroll = 0;

      const elementsToCheck = [
        container,
        document.documentElement,
        document.body,
      ];

      for (const el of elementsToCheck) {
        if (!el) continue;

        let st = 0;
        let sh = 0;
        let ch = 0;

        if (el === document.documentElement || el === document.body) {
          st =
            window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop;
          sh = Math.max(
            document.documentElement.scrollHeight,
            document.body.scrollHeight,
          );
          ch = window.innerHeight;
        } else {
          st = el.scrollTop;
          sh = el.scrollHeight;
          ch = el.clientHeight;
        }

        const ms = sh - ch;
        if (ms > bestMaxScroll) {
          bestMaxScroll = ms;
          bestScrollTop = st;
        }
      }

      let progress =
        bestMaxScroll > 0 ? (bestScrollTop / bestMaxScroll) * 100 : 0;
      progress = Math.max(0, Math.min(100, progress));

      progressRef.current.style.width = `${progress}%`;
    };

    const container = document.getElementById(scrollContainerId);
    container?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Poll briefly to catch Suspense-loaded content resizing the DOM
    const intervalId = setInterval(handleScroll, 500);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 3000);

    handleScroll();

    return () => {
      container?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [scrollContainerId]);

  return (
    <div
      className="absolute bottom-0 left-0 h-[4px] bg-primary z-50 rounded-r-full shadow-sm"
      style={{ width: '0%', transition: 'width 0.1s ease-out' }}
      ref={progressRef}
      aria-hidden="true"
    />
  );
}
